<template>
  <div class="user-settings-page">
    <!-- App Header -->
    <AppHeader title="User Settings" class="app-header" />

    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <div class="loading-text">Loading user settings...</div>
    </div>

    <!-- Error Container -->
    <div v-else-if="error" class="error-container">
      <div class="error-message">{{ error }}</div>
      <button class="action-button" @click="fetchAllData">Retry</button>
    </div>

    <!-- Main Content Grid - 2 rows, 2 columns like DeviceDetailsPage -->
    <div v-else class="container-fluid">
      <!-- Top Row -->
      <div class="row h-50">
        <!-- Left Column - User Data -->
        <div class="col-md-6 section-col p-0">
          <div class="section-content">
            <div class="section-header section-header-alt">
              <div class="section-title">USER DATA :</div>
              <div class="action-buttons-container">
                <button
                    class="action-button"
                    @click="openUserEditOverlay"
                    v-if="!userDataLoading && userData"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                  Edit
                </button>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="userDataLoading" class="loading-state">
              <div class="loading-spinner"></div>
              <span>Loading user data...</span>
            </div>

            <!-- Error State -->
            <div v-else-if="userDataError" class="error-state">
              <div class="error-message">{{ userDataError }}</div>
              <button class="retry-button" @click="fetchUserData">Retry</button>
            </div>

            <!-- Content State -->
            <div v-else-if="userData" class="device-details-grid">
              <div class="row">
                <!-- Left Column -->
                <div class="col-6">
                  <div class="device-info-row">
                    <div class="device-info-label">EMAIL :</div>
                    <div class="device-info-value">{{ userData.email || 'N/A' }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">FIRST NAME :</div>
                    <div class="device-info-value">{{ userData.first_name || 'N/A' }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">LAST NAME :</div>
                    <div class="device-info-value">{{ userData.last_name || 'N/A' }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">ROLE :</div>
                    <div class="device-info-value">{{ userRoleDisplay }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">STATUS :</div>
                    <div class="device-info-value" :class="getUserStatusClass(userData.status)">
                      {{ userStatusDisplay }}
                    </div>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="col-6">
                  <div class="device-info-row">
                    <div class="device-info-label">USER ID :</div>
                    <div class="device-info-value">{{ userData.user_id || 'N/A' }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">TENANT ID :</div>
                    <div class="device-info-value">{{ userData.tenant_id || 'N/A' }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">CREATED :</div>
                    <div class="device-info-value">{{ formatDate(userData.created_at) }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">LAST LOGIN :</div>
                    <div class="device-info-value">{{ formatDate(userData.last_login_at) }}</div>
                  </div>
                  <div class="device-info-row" v-if="userData.mfa_enabled !== undefined">
                    <div class="device-info-label">MFA ENABLED :</div>
                    <div class="device-info-value" :class="getMfaStatusClass(userData.mfa_enabled)">
                      {{ userData.mfa_enabled ? 'Yes' : 'No' }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="empty-state">
              <div class="info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <p>No user information available.</p>
            </div>
          </div>
        </div>

        <!-- Right Column - Organization Data -->
        <div class="col-md-6 section-col section-col-alt p-0">
          <div class="section-content">
            <div class="section-header section-header-alt">
              <div class="section-title">ORGANIZATION DATA :</div>
              <div class="action-buttons-container">
                <button
                    class="action-button"
                    @click="openOrganizationEditOverlay"
                    v-if="!organizationDataLoading && organizationData"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                  Edit
                </button>
              </div>
            </div>

            <!-- Loading State -->
            <div v-if="organizationDataLoading" class="loading-state">
              <div class="loading-spinner"></div>
              <span>Loading organization data...</span>
            </div>

            <!-- Error State -->
            <div v-else-if="organizationDataError" class="error-state">
              <div class="error-message">{{ organizationDataError }}</div>
              <button class="retry-button" @click="fetchOrganizationData">Retry</button>
            </div>

            <!-- Content State -->
            <div v-else-if="organizationData" class="device-details-grid">
              <div class="row">
                <!-- Left Column -->
                <div class="col-6">
                  <div class="device-info-row">
                    <div class="device-info-label">NAME :</div>
                    <div class="device-info-value">{{ organizationData.name || 'N/A' }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">TENANT ID :</div>
                    <div class="device-info-value">{{ organizationData.tenant_id || 'N/A' }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">STATUS :</div>
                    <div class="device-info-value" :class="getOrganizationStatusClass(organizationData.status)">
                      {{ organizationStatusDisplay }}
                    </div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">CONTACT EMAIL :</div>
                    <div class="device-info-value">{{ organizationData.contact_email || 'N/A' }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">CONTACT PHONE :</div>
                    <div class="device-info-value">{{ organizationData.contact_phone || 'N/A' }}</div>
                  </div>
                </div>

                <!-- Right Column -->
                <div class="col-6">
                  <div class="device-info-row">
                    <div class="device-info-label">ADDRESS :</div>
                    <div class="device-info-value">{{ organizationData.address || 'N/A' }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">CITY :</div>
                    <div class="device-info-value">{{ organizationData.city || 'N/A' }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">COUNTRY :</div>
                    <div class="device-info-value">{{ organizationData.country || 'N/A' }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">CREATED :</div>
                    <div class="device-info-value">{{ formatDate(organizationData.created_at) }}</div>
                  </div>
                  <div class="device-info-row">
                    <div class="device-info-label">UPDATED :</div>
                    <div class="device-info-value">{{ formatDate(organizationData.updated_at) }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="empty-state">
              <div class="info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <p>No organization information available.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Row -->
      <div class="row h-50">
        <!-- Left Column - General Tasks -->
        <div class="col-md-6 section-col p-0">
          <UserSettingPageGeneralTasking />
        </div>

        <!-- Right Column - Future Component -->
        <div class="col-md-6 section-col section-col-alt p-0">
          <div class="section-content">
            <div class="section-header section-header-alt">
              <div class="section-title">FUTURE COMPONENT :</div>
            </div>
            <div class="empty-state">
              <div class="info-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <p>Additional functionality will be added here.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit User Overlay -->
    <div v-if="showUserEditOverlay" class="edit-overlay">
      <div class="edit-overlay-backdrop" @click="closeUserEditOverlay"></div>
      <div class="edit-overlay-content">
        <div class="edit-header">
          <h2>Edit User Data</h2>
          <button class="close-button" @click="closeUserEditOverlay">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="form-container">
          <p>User edit functionality will be implemented here.</p>
        </div>
        <div class="edit-actions">
          <button class="action-button" @click="closeUserEditOverlay">Cancel</button>
          <button class="action-button submit-button" @click="closeUserEditOverlay">Save Changes</button>
        </div>
      </div>
    </div>

    <!-- Edit Organization Overlay -->
    <div v-if="showOrganizationEditOverlay" class="edit-overlay">
      <div class="edit-overlay-backdrop" @click="closeOrganizationEditOverlay"></div>
      <div class="edit-overlay-content">
        <div class="edit-header">
          <h2>Edit Organization Data</h2>
          <button class="close-button" @click="closeOrganizationEditOverlay">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="form-container">
          <p>Organization edit functionality will be implemented here.</p>
        </div>
        <div class="edit-actions">
          <button class="action-button" @click="closeOrganizationEditOverlay">Cancel</button>
          <button class="action-button submit-button" @click="closeOrganizationEditOverlay">Save Changes</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppHeader from './AppHeader.vue';
import UserSettingPageGeneralTasking from './UserSettingPageGeneralTasking.vue';

export default {
  name: 'UserSettingsPage',
  components: {
    AppHeader,
    UserSettingPageGeneralTasking
  },

  data() {
    return {
      // User data
      userData: null,
      userDataLoading: true,
      userDataError: '',

      // Organization data
      organizationData: null,
      organizationDataLoading: true,
      organizationDataError: '',

      // Overall page state
      isLoading: true,
      error: '',

      // Overlay states
      showUserEditOverlay: false,
      showOrganizationEditOverlay: false
    };
  },

  computed: {
    userRoleDisplay() {
      if (!this.userData?.role) return 'N/A';
      return this.userData.role.charAt(0).toUpperCase() + this.userData.role.slice(1);
    },

    userStatusDisplay() {
      if (!this.userData?.status) return 'N/A';
      return this.userData.status.charAt(0).toUpperCase() + this.userData.status.slice(1);
    },

    organizationStatusDisplay() {
      if (!this.organizationData?.status) return 'N/A';
      return this.organizationData.status.charAt(0).toUpperCase() + this.organizationData.status.slice(1);
    }
  },

  created() {
    this.fetchAllData();
  },

  methods: {
    // ==========================================
    // DATA FETCHING METHODS
    // ==========================================
    async fetchAllData() {
      this.isLoading = true;
      this.error = '';

      try {
        await Promise.all([
          this.fetchUserData(),
          this.fetchOrganizationData()
        ]);
      } catch (error) {
        console.error('Error loading user settings:', error);
        this.error = 'Failed to load user settings. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async fetchUserData() {
      this.userDataLoading = true;
      this.userDataError = '';

      try {
        // First try to get user info from localStorage
        const userInfoStr = localStorage.getItem('user_info');
        if (userInfoStr) {
          const localUserData = JSON.parse(userInfoStr);

          // If we have email in localStorage, use the API to get fresh data
          if (localUserData.email) {
            const apiBaseUrl = process.env.VUE_APP_API_URL;
            const encodedEmail = encodeURIComponent(localUserData.email);
            const response = await fetch(`${apiBaseUrl}/users/email/${encodedEmail}`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              this.userData = await response.json();
              return this.userData;
            }
          }

          // Fallback to localStorage data if API call fails
          this.userData = localUserData;
        } else {
          throw new Error('No user information found');
        }

        return this.userData;
      } catch (error) {
        console.error('Error fetching user data:', error);
        this.userDataError = 'Failed to load user data';
        throw error;
      } finally {
        this.userDataLoading = false;
      }
    },

    async fetchOrganizationData() {
      this.organizationDataLoading = true;
      this.organizationDataError = '';

      try {
        const tenantId = localStorage.getItem('tenant_id');
        if (!tenantId) {
          throw new Error('No tenant ID found');
        }

        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const response = await fetch(`${apiBaseUrl}/organizations/${tenantId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Error fetching organization data: ${response.statusText}`);
        }

        this.organizationData = await response.json();
        return this.organizationData;
      } catch (error) {
        console.error('Error fetching organization data:', error);
        this.organizationDataError = 'Failed to load organization data';
        throw error;
      } finally {
        this.organizationDataLoading = false;
      }
    },

    // ==========================================
    // EVENT HANDLERS
    // ==========================================
    handleUserUpdated(updatedUser) {
      this.userData = updatedUser;
      this.closeUserEditOverlay();
    },

    handleOrganizationUpdated(updatedOrganization) {
      this.organizationData = updatedOrganization;
      this.closeOrganizationEditOverlay();
    },

    // ==========================================
    // OVERLAY METHODS
    // ==========================================
    openUserEditOverlay() {
      this.showUserEditOverlay = true;
    },

    closeUserEditOverlay() {
      this.showUserEditOverlay = false;
    },

    openOrganizationEditOverlay() {
      this.showOrganizationEditOverlay = true;
    },

    closeOrganizationEditOverlay() {
      this.showOrganizationEditOverlay = false;
    },

    // ==========================================
    // UTILITY METHODS
    // ==========================================
    formatDate(dateString) {
      if (!dateString) return 'N/A';

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'Error';
      }
    },

    // Status class helper methods
    getUserStatusClass(status) {
      return status === 'active' ? 'status-yes' : 'status-no';
    },

    getMfaStatusClass(mfaEnabled) {
      return mfaEnabled ? 'status-yes' : 'status-no';
    },

    getOrganizationStatusClass(status) {
      return status === 'active' ? 'status-yes' : 'status-no';
    }
  }
};
</script>

<style scoped>
/* ===========================================
   BASE LAYOUT & STRUCTURE - Matching DeviceDetailsPage
   =========================================== */
.user-settings-page {
  height: 100vh;
  color: white;
  font-family: 'Inter', sans-serif;
  position: relative;
  overflow: hidden;
}

/* Fixed header styling */
.app-header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
}

/* Full height/width grid layout - Exactly like DeviceDetailsPage */
.container-fluid {
  height: 100vh;
  width: 100%;
  max-width: 100%;
  padding: 0;
  position: absolute;
  top: 0;
  left: 0;
}

.row {
  margin: 0;
  padding: 0;
}

/* Section columns styling - Matching DeviceDetailsPage */
.section-col {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 24px !important;
}

.section-col-alt {
  background-color: rgba(86, 86, 86, 0.1);
}

/* Section styling - Matching DeviceDetailsInfo.vue */
.section-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
  display: inline-block;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header-alt {
  margin-top: 80px;
}

/* Device details grid - Matching DeviceDetailsInfo.vue */
.device-details-grid {
  margin-top: 35px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.device-info-row {
  margin-bottom: 25px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.device-info-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.05em;
  opacity: 0.6;
  text-align: left;
  text-transform: uppercase;
}

.device-info-value {
  font-size: 11px;
  font-weight: 500;
}

/* Status Colors - Matching DeviceDetailsInfo.vue */
.status-yes {
  color: #4caf50 !important;
}

.status-no {
  color: #f44336 !important;
}

/* ===========================================
   LOADING STATES - Matching DeviceDetailsInfo.vue
   =========================================== */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  gap: 20px;
}

.loading-text {
  color: white;
  font-size: 16px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ===========================================
   ERROR STATES - Matching DeviceDetailsInfo.vue
   =========================================== */
.error-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  gap: 20px;
  padding: 20px;
  text-align: center;
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* ===========================================
   EMPTY STATES - Matching DeviceDetailsInfo.vue
   =========================================== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  opacity: 0.8;
  text-align: center;
}

.info-icon {
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 16px;
}

.empty-state p {
  font-size: 13px;
  max-width: 80%;
  margin-bottom: 8px;
}

/* ===========================================
   BUTTONS & ACTIONS - Matching DeviceDetailsInfo.vue
   =========================================== */
.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.action-buttons-container {
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
}

/* ===========================================
   OVERLAY MODALS - Matching DeviceDetailsEditOverlay.vue
   =========================================== */
.edit-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.edit-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(32, 32, 32, 0.1) 0%, rgba(16, 16, 16, 0.95) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease forwards;
}

.edit-overlay-content {
  position: relative;
  width: 700px;
  max-width: 90%;
  max-height: 90vh;
  border-radius: 8px;
  padding: 24px;
  color: white;
  background-color: rgba(50, 50, 50, 0.2);
  backdrop-filter: blur(16px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  overflow-y: auto;
  animation: slideIn 0.3s ease forwards;
  z-index: 1001;
}

.edit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 16px;
}

.edit-header h2 {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
}

.close-button {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.close-button:hover {
  color: rgba(255, 255, 255, 0.9);
}

.form-container {
  position: relative;
  min-height: 200px;
  padding: 20px 0;
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.submit-button {
  background-color: rgba(79, 195, 247, 0.2);
}

.submit-button:hover {
  background-color: rgba(79, 195, 247, 0.3);
}

/* ===========================================
   ANIMATIONS - Matching other components
   =========================================== */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===========================================
   RESPONSIVE DESIGN - Matching DeviceDetailsPage
   =========================================== */
@media (max-width: 767.98px) {
  .section-header-alt {
    margin-top: 60px;
  }

  .device-info-row {
    margin-bottom: 15px;
  }

  .section-col {
    padding: 15px !important;
  }

  .row {
    height: auto !important;
  }
}
</style>